import { Set } from 'immutable';
import { Action } from '@reduxjs/toolkit';

import { StakingError } from '../saga/staking/types';
import { GetState, ReduxAction, ThunkDispatchType } from 'app/redux/types';

import { MIN_POSSIBLE_STAKE_PRICE } from 'app/constants/userRestrictions';
import type { RecordOfBetEntity, RecordOfSportEntity, RecordOfStakeEntity } from 'app/lib/betting/betting-entities';
import { OpenLoginDialog, openLoginDialog } from 'app/modules/SigningNew/actions';
import { selectIsLoggedIn } from 'app/redux/selectors/auth';

import { AccountType, MAX_POSSIBLE_NUMBER_OF_BETS, SubmissionState } from '../constants';
import {
  RecordOfBetEntryType,
  RecordOfBetInfoType,
  RecordOfMatchEntityType,
  RecordOfMatchEntryType,
  RecordOfSportEntryType,
  RecordOfStakeEntryType,
} from '../records';
import {
  selectAnyHasLockedState,
  selectBets,
  selectDeletesQueue,
  selectHasEntryError,
  selectInsertsQueue,
  selectIsLockedEntry,
  selectMatches,
  selectRequestErrorShowing,
  selectSomeHasError,
  selectSports,
  selectStakes,
} from '../selectors/entries';
import { selectOneClickBetAmount, selectProcessingEntry } from '../selectors/oneClickBet';
import { selectOneClickBetActive } from '../selectors/preferences';
import { selectSingleBetsInfo } from '../selectors/stakeTypes/single';
import { selectConflictState } from '../selectors/state';
import { selectSubmission } from '../selectors/submission';
import {
  createBetEntry,
  createMatchEntry,
  createSingleBetInfo,
  createSportEntry,
  createStakeEntry,
} from '../utils/entries';
import {
  RemoveConflictedEntries,
  removeConflictedEntries, ResolveByCancelInsert,
  resolveByCancelInsert,
} from './conflict';
import { CheckAvailabilitySuccessPayload } from './index';
import { ProcessEntry, processEntry } from './oneClickBet';
import { BetError, MatchError } from './submission';

// ---

export const INSERT_IN_QUEUE_FOR_ADD = 'BET_SLIP:INSERT_IN_QUEUE_FOR_ADD';

type InsertInQueueForAddPayload = {
  // TODO: Почему-то прокидываются названия для спорта, матча и ставки вместо абстрактных id
  // из-за чего невозможно обновлять переводы этих строк в корзине в рантайме
  bet: RecordOfBetEntity;
  stake: RecordOfStakeEntity;
  match: RecordOfMatchEntityType;
  sport: RecordOfSportEntity;
};

export type InsertInQueueForAdd = ReduxAction<typeof INSERT_IN_QUEUE_FOR_ADD, {
  betInfo: RecordOfBetInfoType;
  bet: RecordOfBetEntryType;
  stake: RecordOfStakeEntryType;
  match: RecordOfMatchEntryType;
  sport: RecordOfSportEntryType;
}>;

type InsertInQueueActions =
  | InsertInQueueForAdd
  | OpenLoginDialog
  | ProcessEntry
  | RemoveConflictedEntries
  | ResolveByCancelInsert;

export const INSERT_IN_QUEUE_FOR_DELETE = 'BET_SLIP:INSERT_IN_QUEUE_FOR_DELETE';

export type RemoveEntryProps = {
  betId: number;
  stakeId: number;
  matchId: number;
  sportId: number;
};

export type InsertInQueueForDelete = ReduxAction<typeof INSERT_IN_QUEUE_FOR_DELETE, {
  entries: RemoveEntryProps[];
}>;

// ---

type DeleteAction = (
  dispatch: ThunkDispatchType<InsertInQueueForDelete>,
  getState: GetState,
) => void;

export type DeleteFromQueueAction = (betIds: number[]) => DeleteAction;
export const insertInQueueForDelete: DeleteFromQueueAction = (betIds: number[]) => (
  (dispatch, getState) => {
    const state = getState();

    const bets = selectBets(state);
    const betsInfo = selectSingleBetsInfo(state);

    const deletes = selectDeletesQueue(state);
    const stakes = selectStakes(state);
    const matches = selectMatches(state);
    const sports = selectSports(state);

    const entries = betIds.reduce((acc: RemoveEntryProps[], betId) => {
      if (deletes.has(betId)) {
        console.error(`insertInQueueForDelete -> the deletion of #${betId} is already queued`);
        return acc;
      }

      const betInfo = betsInfo.get(betId);
      const bet = bets.get(betId);
      const stake = bet && stakes.get(bet.entity.stakeId);
      const match = bet && matches.get(bet.entity.matchId);
      const sport = match && sports.get(match.entity.sportId);

      if (!bet || !stake || !match || !betInfo || !sport) {
        console.error(`insertInQueueForDelete -> there's no bet slip entry #${betId}`);
        return acc;
      }

      acc.push({
        betId,
        stakeId: stake.entity.id,
        matchId: match.entity.id,
        sportId: sport.entity.id,
      });
      return acc;
    }, []);

    return dispatch({
      type: INSERT_IN_QUEUE_FOR_DELETE,
      payload: { entries },
    });
  }
);

type InsertOneEntryInQueueForDelete = (betId: number) => DeleteAction;
export const insertOneEntryInQueueForDelete: InsertOneEntryInQueueForDelete = (betId: number) => (
  insertInQueueForDelete([betId])
);

export const insertInQueueForAdd = (payload: InsertInQueueForAddPayload) => (
  (dispatch: ThunkDispatchType<InsertInQueueActions>, getState: GetState): void => {
    const state = getState();

    const betId = payload.bet.id;
    const bets = selectBets(state);

    const { bet, stake, match, sport } = payload;

    const submission = selectSubmission(state);

    if (submission !== SubmissionState.AWAITING) {
      console.warn(`insertInQueueForAdd -> unable to add entry while submission state is ${submission}`);
      return;
    }

    if (selectOneClickBetActive(state)) {
      if (!selectIsLoggedIn(state)) {
        dispatch(openLoginDialog());
        return;
      }

      const amount = selectOneClickBetAmount(state);

      if (amount.value <= MIN_POSSIBLE_STAKE_PRICE) {
        console.warn(`insertInQueueForAdd -> amount value (${amount.value}) <= ${MIN_POSSIBLE_STAKE_PRICE}`);
        return;
      }

      const processingEntry = selectProcessingEntry(state);

      const entrySubmission = processingEntry?.betInfo.submission;
      const isErrorEntrySubmission =
        entrySubmission === SubmissionState.FAILED
        || entrySubmission === SubmissionState.REJECTED;

      if (entrySubmission && !isErrorEntrySubmission) {
        console.warn('insertInQueueForAdd -> one click bet is processing');
        return;
      }

      dispatch(
        processEntry({
          betInfo: createSingleBetInfo(bet),
          bet: createBetEntry(bet),
          stake: createStakeEntry(stake, bet),
          match: createMatchEntry(match, bet),
          sport: createSportEntry(sport, match),
        }),
      );

      return;
    }

    // ---

    // Так как в bets хранится предыдущее состояние,
    // нужно сделать (-1), чтоб получить правильное максимальное значение
    if (bets.size > MAX_POSSIBLE_NUMBER_OF_BETS - 1) {
      console.warn(`insertInQueueForAdd -> max possible number of bets = ${MAX_POSSIBLE_NUMBER_OF_BETS}`);
      return;
    }

    const conflict = selectConflictState(state);

    if (conflict !== null) {
      if (conflict.items.has(betId)) {
        dispatch(resolveByCancelInsert(betId));
      }
      else {
        dispatch(removeConflictedEntries([betId]));
      }
      return;
    }

    if (selectInsertsQueue(state).has(betId)) {
      console.warn('insertInQueueForAdd -> the insertion of #%d is already queued', betId);
      return;
    }

    const hasEntriesErrors = selectSomeHasError(state) || selectAnyHasLockedState(state);

    if (hasEntriesErrors) {
      const isFailedEntry =
        selectRequestErrorShowing(state, betId)
        || selectIsLockedEntry(state, betId);

      if (bets.has(betId) && isFailedEntry) {
        dispatch(insertOneEntryInQueueForDelete(betId));
      }
      else {
        console.warn('You need to remove bets with error');
      }

      return;
    }

    if (bets.has(betId)) {
      dispatch(insertOneEntryInQueueForDelete(betId));
      return;
    }

    dispatch({
      type: INSERT_IN_QUEUE_FOR_ADD,
      payload: {
        betInfo: createSingleBetInfo(bet),
        bet: createBetEntry(bet),
        stake: createStakeEntry(stake, bet),
        match: createMatchEntry(match, bet),
        sport: createSportEntry(sport, match),
      },
    });
  }
);

// ---

export const INSERT_IN_BET_SLIP = 'BET_SLIP:INSERT_IN_BET_SLIP';

type InsertInBetSlipPayload = CheckAvailabilitySuccessPayload & InsertInQueueForAdd['payload'];
export type InsertInBetSlip = ReduxAction<typeof INSERT_IN_BET_SLIP, InsertInBetSlipPayload>;

export const insertInBetSlip = (payload: InsertInBetSlipPayload): InsertInBetSlip => ({
  type: INSERT_IN_BET_SLIP,
  payload,
});

// ---

export const INSERT_FAILED_ENTRY_IN_BET_SLIP = 'BET_SLIP:INSERT_FAILED_ENTRY_IN_BET_SLIP';

export type InsertFailedEntryInBetSlipPayload = {
  matchErrors?: MatchError[];
  betErrors?: BetError[];
} & InsertInQueueForAdd['payload'];

export type InsertFailedEntryInBetSlip = Action<
  typeof INSERT_FAILED_ENTRY_IN_BET_SLIP
> & {
  error: StakingError;
  payload: InsertFailedEntryInBetSlipPayload;
};

export const insertFailedEntryInBetSlip = (
  error: StakingError,
  payload: InsertFailedEntryInBetSlipPayload,
): InsertFailedEntryInBetSlip => ({
  type: INSERT_FAILED_ENTRY_IN_BET_SLIP,
  error,
  payload,
});

// ---

export const CANCEL_INSERT = 'BET_SLIP:CANCEL_INSERT';

export type CancelInsert = ReduxAction<typeof CANCEL_INSERT, {
  betId: number;
}>;

export const cancelInsert = (betId: number): CancelInsert => ({
  type: CANCEL_INSERT,
  payload: { betId },
});

// ---

export const DELETE_FROM_BET_SLIP = 'BET_SLIP:DELETE_FROM_BET_SLIP';

export type DeleteFromBetSlipPayload = Partial<CheckAvailabilitySuccessPayload> & InsertInQueueForDelete['payload'];
export type DeleteFromBetSlip = ReduxAction<typeof DELETE_FROM_BET_SLIP, DeleteFromBetSlipPayload>;

export const deleteFromBetSlip = (payload: DeleteFromBetSlipPayload): DeleteFromBetSlip => ({
  type: DELETE_FROM_BET_SLIP,
  payload,
});

// ---

export const CANCEL_DELETES = 'BET_SLIP:CANCEL_DELETES';

export type CancelDeletes = ReduxAction<typeof CANCEL_DELETES, {
  betIds: number[];
}>;

export const cancelDeletes = (betIds: number[]): CancelDeletes => ({
  type: CANCEL_DELETES,
  payload: { betIds },
});

// ---

export const REPLACE_ENTRIES = 'BET_SLIP:REPLACE_ENTRIES';

type ReplaceEntriesProps = {
  betId: number;
  stakeId: number;
  matchId: number;
  sportId: number;
};

export type ReplaceEntries = ReduxAction<typeof REPLACE_ENTRIES, {
  entries: ReplaceEntriesProps[];
  by: InsertInQueueForAdd['payload'];
}>;

type ReplaceEntriesAction = (entryIds: Set<number>, by: InsertInQueueForAdd['payload']) => (
  dispatch: ThunkDispatchType<ReplaceEntries>,
  getState: GetState,
) => void;

export const replaceEntries: ReplaceEntriesAction = (entryIds, by) => (dispatch, getState) => {
  const state = getState();
  const entryId = by.bet.entity.id;

  if (selectInsertsQueue(state).has(entryId)) {
    console.error(`replaceEntries -> the insertion of #${entryId} is already queued`);
    return;
  }

  const deletes = selectDeletesQueue(state);
  const bets = selectBets(state);
  const stakes = selectStakes(state);
  const matches = selectMatches(state);
  const sports = selectSports(state);
  const betsInfo = selectSingleBetsInfo(state);

  if (bets.has(entryId)) {
    console.error(`replaceEntries -> the bet slip entry #${entryId} is already in`);
    return;
  }

  const entries = entryIds.toArray().reduce((acc: ReplaceEntriesProps[], betId) => {
    if (deletes.has(betId)) {
      console.error(`replaceEntries -> the deletion of #${betId} is already queued`);
      return acc;
    }

    if (!bets.has(betId)) {
      console.error(`replaceEntries -> there's no bet slip entry #${betId}`);
      return acc;
    }

    const betInfo = betsInfo.get(betId);
    const bet = bets.get(betId);
    const stake = bet && stakes.get(bet.entity.stakeId);
    const match = bet && matches.get(bet.entity.matchId);
    const sport = match && sports.get(match.entity.sportId);

    if (!bet || !stake || !match || !betInfo || !sport) {
      console.error(`insertInQueueForDelete -> there's no bet slip entry #${betId}`);
      return acc;
    }

    acc.push({
      betId,
      stakeId: stake.entity.id,
      matchId: match.entity.id,
      sportId: sport.entity.id,
    });
    return acc;
  }, []);

  return dispatch({
    type: REPLACE_ENTRIES,
    payload: { entries, by },
  });
};

// ---

export const COMPLETE_REPLACE = 'BET_SLIP:COMPLETE_REPLACE';

export type CompleteReplacePayload = CheckAvailabilitySuccessPayload & ReplaceEntries['payload'];
export type CompleteReplace = ReduxAction<typeof COMPLETE_REPLACE, CompleteReplacePayload>;

export const completeReplace = (payload: CompleteReplacePayload): CompleteReplace => ({
  type: COMPLETE_REPLACE,
  payload,
});

// ---

export const CANCEL_REPLACE = 'BET_SLIP:CANCEL_REPLACE';

export type CancelReplace = ReduxAction<typeof CANCEL_REPLACE, {
  betIds: number[];
  byBetId: number;
}>;

export const cancelReplace = (betIds: number[], byBetId: number): CancelReplace => ({
  type: CANCEL_REPLACE,
  payload: { betIds, byBetId },
});

// ---

export const CHANGE_ENTRY_AMOUNT = 'BET_SLIP:CHANGE_ENTRY_AMOUNT';

export type ChangeEntryAmount = ReduxAction<typeof CHANGE_ENTRY_AMOUNT, {
  betId: number;
  value: string;
  account: AccountType;
}>;

export const changeEntryAmount = (
  betId: number,
  value: string,
  account: AccountType,
): ChangeEntryAmount => ({
  type: CHANGE_ENTRY_AMOUNT,
  payload: { betId, value, account },
});

// ---

export const SETUP_ENTRY_AMOUNT = 'BET_SLIP:SETUP_ENTRY_AMOUNT';

export type SetupEntryAmount = ReduxAction<typeof SETUP_ENTRY_AMOUNT, {
  betId: number;
  value: string;
  account: AccountType;
}>;

export const setupEntryAmount = (
  betId: number,
  value: string,
  account: AccountType,
): SetupEntryAmount => ({
  type: SETUP_ENTRY_AMOUNT,
  payload: { betId, value, account },
});

// ---

export const SWITCH_TO_TOTAL_AMOUNT = 'BET_SLIP:SWITCH_TO_TOTAL_AMOUNT';

export type SwitchToTotalAmount = ReduxAction<typeof SWITCH_TO_TOTAL_AMOUNT, {
  value: boolean;
}>;

export const switchToTotalAmount = (value: boolean): SwitchToTotalAmount => ({
  type: SWITCH_TO_TOTAL_AMOUNT,
  payload: { value },
});

// ---

export const ACCEPT_PRICE_CHANGES = 'BET_SLIP:ACCEPT_PRICE_CHANGES';

export type AcceptPriceChanges = Action<typeof ACCEPT_PRICE_CHANGES>;

export const acceptPriceChanges = (): AcceptPriceChanges => ({
  type: ACCEPT_PRICE_CHANGES,
});

// ---

export type DeleteUnavailableBets = () => (
  dispatch: ThunkDispatchType<InsertInQueueForDelete>,
  getState: GetState,
) => void;

export const deleteUnavailableBets: DeleteUnavailableBets = () => (dispatch, getState) => {
  const state = getState();

  const betsInfo = selectSingleBetsInfo(state);
  const entries = selectBets(state);
  const stakes = selectStakes(state);
  const matches = selectMatches(state);

  const betIds = entries.reduce((ids: number[], entry) => {
    const stake = stakes.get(entry.entity.stakeId);
    const match = matches.get(entry.entity.matchId);
    const info = betsInfo.get(entry.entity.id);

    if (!(match?.entity.isOpen && stake?.entity.isOpen && entry.entity.isOpen)) {
      ids.push(entry.entity.id);
    }
    else if (selectHasEntryError(info?.error)) {
      ids.push(entry.entity.id);
    }

    return ids;
  }, []);

  return dispatch(insertInQueueForDelete(betIds));
};
